import { memo, useMemo } from 'react'
import {
  Box,
  Heading,
  HStack,
  VStack,
  Text,
  ButtonGroup,
  Button,
  Select,
  useColorModeValue,
  Tooltip as ChakraTooltip,
  Spinner,
  useBreakpointValue,
} from '@chakra-ui/react'
import {
  BarChart,
  Bar,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts'
import { colors, components } from '../../theme'
import { STRINGS } from '../../resources'
import type { Vulnerability } from '../../types/vulnerability'
import type { ChartBucket } from '../../workers/dataWorker'
import styles from './Charts.module.css'

type TimeGranularity = 'year' | 'month' | 'week' | 'day'
type ChartType = 'bar' | 'area'
type TimeRange = '7d' | '30d' | '90d' | '12m' | 'all'

interface ChartsProps {
  data: Vulnerability[]
  chartData: ChartBucket[]  // Pre-computed by worker, no main thread computation needed
  totalCount: number
  hasActiveFilters: boolean
  severityFilters: string[]
  onSeverityClick: (severity: 'critical' | 'high' | 'medium' | 'low') => void
  legendCounts: Record<string, number>  // Severity counts before severity filter (for legend)
  chartType: ChartType
  timeRange: TimeRange
  onChartTypeChange: (type: ChartType) => void
  onTimeRangeChange: (range: TimeRange) => void
  onBucketClick?: (bucketKey: string, bucketLabel: string) => void
  selectedBucket?: string | null
  isFiltering?: boolean
}

// Export for use in App.tsx filtering
export { getAutoGranularity }
export type { TimeGranularity }

// Use shared severity colors from design tokens
const SEVERITY_COLORS = colors.severity

const SEVERITY_CONFIG = [
  { key: 'critical', label: STRINGS.SEVERITY.CRITICAL },
  { key: 'high', label: STRINGS.SEVERITY.HIGH },
  { key: 'medium', label: STRINGS.SEVERITY.MEDIUM },
  { key: 'low', label: STRINGS.SEVERITY.LOW },
] as const

const TIME_RANGE_OPTIONS: { value: TimeRange; label: string }[] = [
  { value: '7d', label: 'Last 7 days' },
  { value: '30d', label: 'Last 30 days' },
  { value: '90d', label: 'Last 90 days' },
  { value: '12m', label: 'Last 12 months' },
  { value: 'all', label: 'All time' },
]

// Auto-calculate optimal granularity based on time range
function getAutoGranularity(timeRange: TimeRange): TimeGranularity {
  switch (timeRange) {
    case '7d':
      return 'day'
    case '30d':
      return 'day'
    case '90d':
      return 'week'
    case '12m':
      return 'month'
    case 'all':
      return 'month'
    default:
      return 'month'
  }
}

export const Charts = memo(function Charts({
  data,
  chartData,  // Pre-computed by worker - no main thread loops!
  totalCount,
  hasActiveFilters,
  severityFilters,
  onSeverityClick,
  legendCounts,
  chartType,
  timeRange,
  onChartTypeChange,
  onTimeRangeChange,
  onBucketClick,
  selectedBucket,
  isFiltering = false,
}: ChartsProps) {
  const cardBg = useColorModeValue(colors.ui.card.light, colors.ui.card.dark)
  const textColor = useColorModeValue(colors.ui.text.secondary.light, colors.ui.text.secondary.dark)
  const overlayBg = useColorModeValue('whiteAlpha.700', 'blackAlpha.600')
  const selectBorderColor = useColorModeValue('gray.300', 'gray.600')

  // Responsive breakpoints
  const isMobile = useBreakpointValue({ base: true, md: false }) ?? false
  const isSmallMobile = useBreakpointValue({ base: true, sm: false }) ?? false

  // Handle bar click
  const handleBarClick = (data: { bucketKey: string; label: string }) => {
    if (onBucketClick && data.bucketKey) {
      onBucketClick(data.bucketKey, data.label)
    }
  }

  // Calculate dynamic X-axis label interval
  const xAxisInterval = useMemo(() => {
    const dataPoints = chartData.length
    const maxLabels = isMobile ? (isSmallMobile ? 4 : 6) : 10

    if (dataPoints <= maxLabels) return 0
    return Math.ceil(dataPoints / maxLabels) - 1
  }, [chartData.length, isMobile, isSmallMobile])

  return (
    <Box className={styles.container} bg={cardBg}>
      <VStack align="stretch" spacing={3}>
        <div className={styles.header}>
          <div className={styles.controlsWrapper}>
            <Heading className={styles.title} fontSize={components.chart.title.fontSize} fontWeight={components.chart.title.fontWeight}>
              {STRINGS.CHARTS.VULNERABILITIES_OVER_TIME}
            </Heading>
            <HStack spacing={2}>
              <Select
                size="sm"
                width="auto"
                minW="140px"
                value={timeRange}
                onChange={(e) => onTimeRangeChange(e.target.value as TimeRange)}
                bg={cardBg}
                borderColor={selectBorderColor}
              >
                {TIME_RANGE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Select>
              <ButtonGroup size="xs" isAttached variant="outline">
                <ChakraTooltip label="Bar chart" hasArrow isDisabled={isMobile}>
                  <Button
                    onClick={() => onChartTypeChange('bar')}
                    variant={chartType === 'bar' ? 'solid' : 'outline'}
                    colorScheme={chartType === 'bar' ? 'blue' : 'gray'}
                  >
                    Bar
                  </Button>
                </ChakraTooltip>
                <ChakraTooltip label="Area chart" hasArrow isDisabled={isMobile}>
                  <Button
                    onClick={() => onChartTypeChange('area')}
                    variant={chartType === 'area' ? 'solid' : 'outline'}
                    colorScheme={chartType === 'area' ? 'blue' : 'gray'}
                  >
                    Area
                  </Button>
                </ChakraTooltip>
              </ButtonGroup>
            </HStack>
          </div>
          {hasActiveFilters && (
            <Text className={styles.filterCount} color={textColor}>
              {STRINGS.CHARTS.SHOWING} <strong className={styles.filterCountHighlight}>{data.length.toLocaleString()}</strong> {STRINGS.TABLE.OF} {totalCount.toLocaleString()}
            </Text>
          )}
        </div>
      </VStack>

      <div className={styles.chartWithLegend}>
        <Box className={styles.chartWrapper} position="relative">
          {isFiltering && (
            <Box
              position="absolute"
              top={0}
              left={0}
              right={0}
              bottom={0}
              bg={overlayBg}
              display="flex"
              alignItems="center"
              justifyContent="center"
              zIndex={10}
              borderRadius="md"
            >
              <Spinner size="lg" color="blue.500" thickness="3px" />
            </Box>
          )}
          <ResponsiveContainer width="100%" height="100%">
            {chartType === 'bar' ? (
              <BarChart data={chartData} onClick={(e) => e?.activePayload?.[0]?.payload && handleBarClick(e.activePayload[0].payload)} style={{ cursor: 'pointer' }}>
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: isMobile ? 8 : 10, fill: textColor }}
                  interval={xAxisInterval}
                  angle={-45}
                  textAnchor="end"
                  height={isMobile ? 40 : 50}
                />
                <YAxis tickFormatter={(v) => v.toLocaleString()} tick={{ fontSize: 11, fill: textColor }} />
                <RechartsTooltip
                  formatter={(value: number, name: string) => [value.toLocaleString(), name]}
                  contentStyle={{
                    backgroundColor: cardBg,
                    borderColor: textColor,
                  }}
                />
                {severityFilters.length > 0 ? (
                  <>
                    <Bar dataKey="critical" stackId="a" fill={SEVERITY_COLORS.critical} name={STRINGS.SEVERITY.CRITICAL}>
                      {chartData.map((entry, index) => (
                        <Cell key={index} fillOpacity={selectedBucket && entry.bucketKey !== selectedBucket ? 0.3 : 1} />
                      ))}
                    </Bar>
                    <Bar dataKey="high" stackId="a" fill={SEVERITY_COLORS.high} name={STRINGS.SEVERITY.HIGH}>
                      {chartData.map((entry, index) => (
                        <Cell key={index} fillOpacity={selectedBucket && entry.bucketKey !== selectedBucket ? 0.3 : 1} />
                      ))}
                    </Bar>
                    <Bar dataKey="medium" stackId="a" fill={SEVERITY_COLORS.medium} name={STRINGS.SEVERITY.MEDIUM}>
                      {chartData.map((entry, index) => (
                        <Cell key={index} fillOpacity={selectedBucket && entry.bucketKey !== selectedBucket ? 0.3 : 1} />
                      ))}
                    </Bar>
                    <Bar dataKey="low" stackId="a" fill={SEVERITY_COLORS.low} name={STRINGS.SEVERITY.LOW}>
                      {chartData.map((entry, index) => (
                        <Cell key={index} fillOpacity={selectedBucket && entry.bucketKey !== selectedBucket ? 0.3 : 1} />
                      ))}
                    </Bar>
                  </>
                ) : (
                  <Bar dataKey="total" fill={colors.chart.total} name={STRINGS.CHARTS.TOTAL} radius={[4, 4, 0, 0]}>
                    {chartData.map((entry, index) => (
                      <Cell key={index} fillOpacity={selectedBucket && entry.bucketKey !== selectedBucket ? 0.3 : 1} />
                    ))}
                  </Bar>
                )}
              </BarChart>
            ) : (
              <AreaChart data={chartData} onClick={(e) => e?.activePayload?.[0]?.payload && handleBarClick(e.activePayload[0].payload)} style={{ cursor: 'pointer' }}>
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: isMobile ? 8 : 10, fill: textColor }}
                  interval={xAxisInterval}
                  angle={-45}
                  textAnchor="end"
                  height={isMobile ? 40 : 50}
                />
                <YAxis tickFormatter={(v) => v.toLocaleString()} tick={{ fontSize: 11, fill: textColor }} />
                <RechartsTooltip
                  formatter={(value: number, name: string) => [value.toLocaleString(), name]}
                  contentStyle={{
                    backgroundColor: cardBg,
                    borderColor: textColor,
                  }}
                />
                {severityFilters.length > 0 ? (
                  <>
                    <Area dataKey="critical" stackId="a" fill={SEVERITY_COLORS.critical} stroke={SEVERITY_COLORS.critical} fillOpacity={selectedBucket ? 0.3 : 0.7} name={STRINGS.SEVERITY.CRITICAL} />
                    <Area dataKey="high" stackId="a" fill={SEVERITY_COLORS.high} stroke={SEVERITY_COLORS.high} fillOpacity={selectedBucket ? 0.3 : 0.7} name={STRINGS.SEVERITY.HIGH} />
                    <Area dataKey="medium" stackId="a" fill={SEVERITY_COLORS.medium} stroke={SEVERITY_COLORS.medium} fillOpacity={selectedBucket ? 0.3 : 0.7} name={STRINGS.SEVERITY.MEDIUM} />
                    <Area dataKey="low" stackId="a" fill={SEVERITY_COLORS.low} stroke={SEVERITY_COLORS.low} fillOpacity={selectedBucket ? 0.3 : 0.7} name={STRINGS.SEVERITY.LOW} />
                  </>
                ) : (
                  <Area dataKey="total" fill={colors.chart.total} stroke={colors.chart.total} fillOpacity={selectedBucket ? 0.3 : 0.7} name={STRINGS.CHARTS.TOTAL} />
                )}
              </AreaChart>
            )}
          </ResponsiveContainer>
        </Box>

        <div className={styles.legendSide}>
          {SEVERITY_CONFIG.map(({ key, label }) => {
            const isActive = severityFilters.includes(key)
            const color = SEVERITY_COLORS[key]
            return (
              <div
                key={key}
                role="button"
                tabIndex={0}
                className={`${styles.legendItem} ${isActive ? styles.legendItemActive : ''}`}
                style={isActive ? { borderLeftColor: color } : undefined}
                onClick={() => onSeverityClick(key)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onSeverityClick(key) }}
              >
                <div className={styles.legendDot} style={{ backgroundColor: color }} />
                <span className={styles.legendLabel}>
                  {label} ({(legendCounts[key] ?? 0).toLocaleString()})
                </span>
              </div>
            )
          })}
        </div>
      </div>
    </Box>
  )
})

